// Customer-side order queries. RLS scopes every row to the logged-in
// user, so we don't have to pass user_id — the DB does the filtering.
//
// Never leaks internal-only columns (paid_method_details, admin notes,
// etc.). The select list here is what the UI actually renders.

import { supabase } from './supabase'

// `is_paid` is not stored — it's derived from paid_at.
const SELECT = `
  id, order_number, service, service_id,
  bags, pickup_date, pickup_slot, return_date, return_slot, return_tbd,
  address_line1, address_line2, city, state, zip, hotel_name, room_number,
  notes, status,
  weighed_lbs, final_total_cents, handling_fee_pct,
  paid_at, paid_method,
  delivery_photo_path,
  wf_discount_type, wf_discount_value, wf_discount_reason,
  pickup_group_id,
  created_at, updated_at
`

function withDerived(row) {
  if (!row) return row
  return { ...row, is_paid: !!row.paid_at }
}

export async function listMyOrders({ limit = 50 } = {}) {
  const { data, error } = await supabase
    .from('orders')
    .select(SELECT)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data || []).map(withDerived)
}

// LAST-RESORT fallback only, for when the RPC below can't be reached. It is
// NOT the source of truth: that is `services.monthly_included_lbs`, read by
// member_cycle_usage(). Never render this number on a path where the RPC
// succeeded — the whole point of the RPC is that no screen quotes an
// allowance we don't actually give.
export const CYCLE_INCLUDED_LBS = 50

// How many lbs this member has used in the current cycle.
//
// Calls the member_cycle_usage() RPC. It used to recompute the 28-day window
// here in JS with a hard-coded 50, which made this the FOURTH place in the
// codebase that answered "how many lbs has this member used" — and the
// fourth answer. Three of them disagreed (calendar month vs 28-day cycle,
// members-only vs all services), so the number a customer read could differ
// from the number they were billed.
//
// Now Postgres answers, once, for every screen. The window (including the
// first-cycle rule) lives in member_cycle_window().
//
// `excludeOrderId` leaves one order out of the sum — the admin ticket flow
// needs it so re-saving a ticket doesn't count its own weight twice.
export async function getMemberCycleUsage(userId = null, excludeOrderId = null) {
  let uid = userId
  if (!uid) {
    const { data } = await supabase.auth.getSession()
    uid = data?.session?.user?.id || null
  }
  if (!uid) throw new Error('Not signed in')

  const { data, error } = await supabase.rpc('member_cycle_usage', {
    p_user: uid,
    p_exclude_order: excludeOrderId,
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  if (!row) throw new Error('No cycle row')

  const cycleUsed = Number(row.cycle_used) || 0
  return {
    cycleUsed,
    cycleIncluded: Number(row.cycle_included) || CYCLE_INCLUDED_LBS,
    cycleLeft: Number(row.cycle_left) || 0,
    cycleOverage: Number(row.cycle_overage) || 0,
    cycleStart: row.cycle_start ? new Date(row.cycle_start) : null,
    cycleEnd: row.cycle_end ? new Date(row.cycle_end) : null,
    // Back-compat with existing callers — kept until they migrate.
    usedLbs: cycleUsed,
  }
}

export async function getMyOrder(orderNumber) {
  if (!orderNumber) return null
  const { data, error } = await supabase
    .from('orders')
    .select(SELECT)
    .eq('order_number', orderNumber)
    .maybeSingle()
  if (error) throw error
  return withDerived(data)
}
